import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import FeatherIcon from '../common/FeatherIcon';
import usePins, { normalizePinPath, pinInitials } from './usePins';
import './pins.css';

/**
 * The "Pinned" block at the top of the sidebar.
 *
 * variant="list"  full rows (label + module caption + options menu) — the
 *                 expanded classic sidebar and the mobile drawer.
 * variant="rail"  40px initials tiles under a star header — the collapsed
 *                 classic sidebar and the new sidebar's icon rail. The header
 *                 opens the same list in a panel beside the rail, where pins
 *                 are renamed, reordered and removed.
 */

/** The pin the current location is: an exact path+search match wins over a pathname match. */
const useActivePinId = (pins) => {
  const location = useLocation();
  return useMemo(() => {
    const here = normalizePinPath(`${location.pathname || ''}${location.search || ''}`);
    const herePath = here.split('?')[0];
    const exact = pins.find((p) => p.path === here);
    if (exact) return exact.id;
    const loose = pins.find((p) => !p.path.includes('?') && p.path === herePath);
    return loose ? loose.id : null;
  }, [pins, location.pathname, location.search]);
};

const captionFor = (pin, t) => {
  const savedView = pin.path.includes('?');
  if (savedView && pin.module) return `${pin.module} · ${t('nav:pins.savedView')}`;
  if (savedView) return t('nav:pins.savedView');
  return pin.module;
};

const focusables = (root) => (root
  ? Array.from(root.querySelectorAll('[role="menuitem"]:not([disabled])'))
  : []);

const PinRow = ({ pin, index, total, active, onUnpin, onRename, onMove, onNavigate }) => {
  const { t } = useTranslation('nav');
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(pin.label);
  const rowRef = useRef(null);
  const menuRef = useRef(null);
  const menuBtnRef = useRef(null);
  const inputRef = useRef(null);
  const cancelledRef = useRef(false);
  const menuId = useId();
  const caption = captionFor(pin, t);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const first = focusables(menuRef.current)[0];
    if (first) first.focus();
    const onDown = (e) => {
      if (rowRef.current && !rowRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const closeMenu = (refocus = true) => {
    setMenuOpen(false);
    if (refocus && menuBtnRef.current) menuBtnRef.current.focus();
  };

  const onMenuKeyDown = (e) => {
    const items = focusables(menuRef.current);
    const at = items.indexOf(document.activeElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeMenu();
    } else if (e.key === 'ArrowDown' && items.length) {
      e.preventDefault();
      items[(at + 1) % items.length].focus();
    } else if (e.key === 'ArrowUp' && items.length) {
      e.preventDefault();
      items[(at - 1 + items.length) % items.length].focus();
    } else if (e.key === 'Home' && items.length) {
      e.preventDefault();
      items[0].focus();
    } else if (e.key === 'End' && items.length) {
      e.preventDefault();
      items[items.length - 1].focus();
    } else if (e.key === 'Tab') {
      setMenuOpen(false);
    }
  };

  const startRename = () => {
    setMenuOpen(false);
    setDraft(pin.label);
    cancelledRef.current = false;
    setEditing(true);
  };

  const finishRename = (save) => {
    if (save && !cancelledRef.current) onRename(pin.id, draft);
    setEditing(false);
    setTimeout(() => menuBtnRef.current && menuBtnRef.current.focus(), 0);
  };

  return (
    <li
      ref={rowRef}
      className={`pins-row${active ? ' active' : ''}${menuOpen ? ' menu-open' : ''}`}
    >
      <div className="pins-row-main">
        {editing ? (
          <input
            ref={inputRef}
            className="pins-row-input"
            value={draft}
            maxLength={80}
            aria-label={t('nav:pins.renameInput', { label: pin.label })}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                finishRename(true);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                cancelledRef.current = true;
                finishRename(false);
              }
            }}
            onBlur={() => { if (!cancelledRef.current) finishRename(true); }}
          />
        ) : (
          <Link
            to={pin.path}
            className="pins-row-link"
            aria-current={active ? 'page' : undefined}
            onClick={onNavigate}
          >
            <span className="pins-row-label" title={pin.label}>{pin.label}</span>
            {caption && <span className="pins-row-module" title={caption}>{caption}</span>}
          </Link>
        )}
        <button
          ref={menuBtnRef}
          type="button"
          className="pins-row-menu-btn"
          aria-label={t('nav:pins.options', { label: pin.label })}
          title={t('nav:pins.options', { label: pin.label })}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-controls={menuOpen ? menuId : undefined}
          onClick={() => (menuOpen ? closeMenu(false) : setMenuOpen(true))}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' && !menuOpen) {
              e.preventDefault();
              setMenuOpen(true);
            }
          }}
        >
          <FeatherIcon name="more-horizontal" size={14} aria-hidden="true" />
        </button>
      </div>
      {menuOpen && (
        // eslint-disable-next-line jsx-a11y/interactive-supports-focus
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={t('nav:pins.options', { label: pin.label })}
          className="pins-row-menu"
          onKeyDown={onMenuKeyDown}
        >
          <button type="button" role="menuitem" tabIndex={-1} onClick={startRename}>
            {t('nav:pins.rename')}
          </button>
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            disabled={index === 0}
            onClick={() => { onMove(pin.id, 'up'); closeMenu(); }}
          >
            {t('nav:pins.moveUp')}
          </button>
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            disabled={index === total - 1}
            onClick={() => { onMove(pin.id, 'down'); closeMenu(); }}
          >
            {t('nav:pins.moveDown')}
          </button>
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            className="danger"
            onClick={() => { setMenuOpen(false); onUnpin(pin.id); }}
          >
            {t('nav:pins.unpin')}
          </button>
        </div>
      )}
    </li>
  );
};

const PinsHead = ({ count, max, as: Tag = 'div', headingId }) => {
  const { t } = useTranslation('nav');
  return (
    <Tag className="pins-block-head">
      <span className="pins-block-title" id={headingId}>
        <FeatherIcon name="star" size={12} fill="currentColor" aria-hidden="true" />
        {t('nav:pins.heading')}
      </span>
      <span className="pins-block-count">{t('nav:pins.count', { count, max })}</span>
    </Tag>
  );
};

const PinsList = ({ pins, activeId, api, onNavigate }) => (
  <ul className="pins-list">
    {pins.map((pin, i) => (
      <PinRow
        key={pin.id}
        pin={pin}
        index={i}
        total={pins.length}
        active={pin.id === activeId}
        onUnpin={api.unpin}
        onRename={api.rename}
        onMove={api.move}
        onNavigate={onNavigate}
      />
    ))}
  </ul>
);

const PinnedList = () => {
  const { t } = useTranslation('nav');
  const api = usePins();
  const activeId = useActivePinId(api.pins);
  const headingId = useId();
  return (
    <section className="pins-block" aria-labelledby={headingId}>
      <PinsHead count={api.pins.length} max={api.max} headingId={headingId} />
      {api.pins.length === 0
        ? <p className="pins-block-empty">{t('nav:pins.empty')}</p>
        : <PinsList pins={api.pins} activeId={activeId} api={api} />}
    </section>
  );
};

const PinnedRail = () => {
  const { t } = useTranslation('nav');
  const api = usePins();
  const activeId = useActivePinId(api.pins);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0 });
  const headRef = useRef(null);
  const panelRef = useRef(null);
  const panelId = useId();
  const headingId = useId();

  const place = () => {
    const el = headRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const rail = el.closest('.sidebar') || el;
    const railRect = rail.getBoundingClientRect();
    const top = Math.max(16, Math.min(rect.top, window.innerHeight - 320));
    const rtl = document.documentElement.getAttribute('dir') === 'rtl';
    setPos(rtl
      ? { top, right: window.innerWidth - railRect.left + 4, left: undefined }
      : { top, left: railRect.right + 4, right: undefined });
  };

  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (headRef.current && headRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        if (headRef.current) headRef.current.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const countText = t('nav:pins.count', { count: api.pins.length, max: api.max });

  return (
    <div className="pins-rail">
      <button
        ref={headRef}
        type="button"
        className={`pins-rail-head${open ? ' open' : ''}`}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={`${t('nav:pins.manage')} (${countText})`}
        title={`${t('nav:pins.heading')} · ${countText}`}
        onClick={() => {
          if (!open) place();
          setOpen(!open);
        }}
      >
        <FeatherIcon name="star" size={16} fill={api.pins.length ? 'currentColor' : 'none'} aria-hidden="true" />
        <span className="pins-rail-head-label">{t('nav:pins.heading')}</span>
      </button>
      {api.pins.length > 0 && (
        <ul className="pins-rail-tiles">
          {api.pins.map((pin) => {
            const caption = captionFor(pin, t);
            const name = caption ? `${pin.label} · ${caption}` : pin.label;
            return (
              <li key={pin.id}>
                <Link
                  to={pin.path}
                  className={`pins-tile${pin.id === activeId ? ' active' : ''}`}
                  title={name}
                  aria-label={name}
                  aria-current={pin.id === activeId ? 'page' : undefined}
                >
                  <span aria-hidden="true">{pinInitials(pin.label)}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
      {open && ReactDOM.createPortal(
        <section
          ref={panelRef}
          id={panelId}
          className="pins-flyout"
          aria-labelledby={headingId}
          style={{ top: pos.top, left: pos.left, right: pos.right, maxHeight: `calc(100vh - ${pos.top}px - 16px)` }}
        >
          <PinsHead count={api.pins.length} max={api.max} headingId={headingId} />
          {api.pins.length === 0
            ? <p className="pins-block-empty">{t('nav:pins.empty')}</p>
            : <PinsList pins={api.pins} activeId={activeId} api={api} onNavigate={() => setOpen(false)} />}
        </section>,
        document.body,
      )}
    </div>
  );
};

const PinnedNavBlock = ({ variant = 'list' }) => (variant === 'rail' ? <PinnedRail /> : <PinnedList />);

export default PinnedNavBlock;
